import React, { useState } from 'react';
import { Button } from './ui/button';
import { Mail, Lock, User, Crown, Star, Key } from 'lucide-react';
import GrantitudeSpiral from './GrantitudeSpiral';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthSystem = ({ onAuthenticated }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    invite_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [spiralPhase, setSpiralPhase] = useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setSpiralPhase(prev => (prev + 0.3) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? 
        { email: formData.email, password: formData.password } :
        { 
          name: formData.name, 
          email: formData.email, 
          password: formData.password,
          invite_code: formData.invite_code
        };

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Store auth data
      localStorage.setItem('planetary_fed_token', response.data.access_token);
      localStorage.setItem('planetary_fed_user', JSON.stringify(response.data.user));
      
      onAuthenticated(response.data.user);
      
    } catch (error) {
      console.error('Auth error:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail || 'Invalid timeline coordinates or invite code');
      } else {
        alert(isLogin ? 'Timeline access denied' : 'Registration failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/8tvqc3ho_ChatGPT%20Image%20Sep%2013%2C%202025%20at%2002_42_22%20AM.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Grant spiral behind background */}
      <div className="absolute inset-0 pointer-events-none opacity-15" style={{ zIndex: 1 }}>
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{
            transform: `scale(8) rotate(${spiralPhase}deg)`,
            filter: 'blur(6px) saturate(1.5) brightness(1.3)',
            mixBlendMode: 'screen'
          }}
        >
          <GrantitudeSpiral size={400} />
        </div>
      </div>

      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-transparent to-black/70"></div>

      <div className="relative z-10 w-full max-w-md mx-auto p-6">
        <div className="bg-black/40 backdrop-blur-2xl border border-white/20 rounded-3xl p-8 shadow-2xl">
          
          <div className="text-center mb-8">
            <div className="flex justify-center items-center space-x-3 mb-4">
              <Crown className="w-10 h-10 text-yellow-400" />
              <h1 
                className="text-3xl font-bold text-white"
                style={{ 
                  fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.4)'
                }}
              >
                {isLogin ? 'Timeline Access' : 'Join the Timeline'}
              </h1>
              <Star className="w-10 h-10 text-cyan-400" />
            </div>
            
            <p 
              className="text-white/80 text-lg"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {isLogin ? 'Access your consciousness journey' : 'Secure multiplayer consciousness access'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            
            {!isLogin && (
              <>
                <div>
                  <label className="block text-white/80 text-lg mb-2" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                    Invite Code
                  </label>
                  <div className="relative">
                    <Key className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                    <input
                      type="text"
                      value={formData.invite_code}
                      onChange={(e) => setFormData({...formData, invite_code: e.target.value.replace(/\D/g, '')})}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white text-lg focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20 text-center font-mono tracking-wider"
                      style={{ fontFamily: '"Courier New", monospace' }}
                      placeholder="Cell phone or custom code"
                      required
                    />
                  </div>
                  <p className="text-cyan-300 text-sm mt-2">
                    Access is invite-only. Use your cell phone number or custom invite code.
                  </p>
                </div>

                <div>
                  <label className="block text-white/80 text-lg mb-2" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                    Name
                  </label>
                  <div className="relative">
                    <User className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white text-lg focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                      placeholder="Your timeline identity"
                      required
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-white/80 text-lg mb-2" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                Email or Cell Phone
              </label>
              <div className="relative">
                <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                <input
                  type="text"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white text-lg focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  placeholder="email@domain.com or 6086321347"
                  required
                />
              </div>
              <p className="text-cyan-300 text-sm mt-2">Founding Players can login with cell phone number</p>
            </div>

            <div>
              <label className="block text-white/80 text-lg mb-2" style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}>
                Password
              </label>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-white/60" />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/30 rounded-xl text-white text-lg focus:border-white/50 focus:outline-none focus:ring-2 focus:ring-white/20"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  placeholder="Access key"
                  required
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white/20 hover:bg-white/30 border-2 border-white/30 hover:border-white/50 text-white h-14 text-xl font-medium transition-all duration-500"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 mr-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></div>
                  {isLogin ? 'Accessing timeline...' : 'Joining timeline...'}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  {isLogin ? (
                    <>
                      <Crown className="w-6 h-6 mr-3" />
                      Access Timeline
                    </>
                  ) : (
                    <>
                      <Key className="w-6 h-6 mr-3" />
                      Join Multiplayer Timeline
                    </>
                  )}
                </div>
              )}
            </Button>
          </form>

          <div className="text-center mt-6">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-white/70 hover:text-white text-sm transition-colors"
              style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
            >
              {isLogin ? 'Need invite code? Join the timeline' : 'Already have access? Enter timeline'}
            </button>
          </div>
          
          {!isLogin && (
            <div className="mt-6 p-4 bg-yellow-600/20 border border-yellow-400/40 rounded-xl">
              <p 
                className="text-yellow-200 text-sm text-center"
                style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
              >
                Invite codes grant access to multiplayer consciousness salons, trust analytics, and collaborative reality creation.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthSystem;